import React from 'react';
import { useTranslation } from 'react-i18next';
import { Braces } from 'lucide-react';
import { useRequestUiStore } from '../store/requestUiStore';

/**
 * "{ } Variables" — the request header's toggle for the variables panel.
 *
 * Lives beside Save and deliberately reuses Save's own `request-header-save`
 * class so the two are the same control shape (padding, radius, hover, and the
 * dark-mode colours defined once in index.css) without duplicating the rules.
 * The extra `request-header-variables` class carries the one thing Save does
 * not have: an ON state, because this is a toggle and the user has to be able
 * to see that it is on.
 *
 * State comes from `requestUiStore`, not from RequestBuilder — the whole point
 * is that it survives RequestBuilder unmounting (page switch) and an app
 * restart. Its own component so RequestBuilder does not grow another block.
 */
export default function RequestVariablesToggle() {
  const { t } = useTranslation();
  const open = useRequestUiStore((s) => s.variablesPanelOpen);
  const toggle = useRequestUiStore((s) => s.toggleVariablesPanel);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={open}
      aria-label={t('request.variables')}
      title={t('request.variablesToggleTitle')}
      className={`request-header-save request-header-variables inline-flex items-center gap-2 px-3 py-1 rounded-md bg-transparent transition-colors text-xs font-medium flex-shrink-0 ${
        open ? 'is-on' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
      }`}
    >
      <Braces size={14} strokeWidth={2} />
      <span className="hidden sm:inline">{t('request.variables')}</span>
    </button>
  );
}
